import React, { useState } from 'react';
import Icon from './AppIcon';

/**
 * Reviewer directory opt-in: a toggle to be findable as a reviewer, plus
 * expertise keywords (suggested chips + free-text) used by the conference
 * reviewer search. Reusable in registration and profile settings.
 *
 * Props:
 *   available: boolean
 *   keywords: string[]
 *   onAvailableChange: (bool) => void
 *   onKeywordsChange: (string[]) => void
 */
const SUGGESTED_FIELDS = [
  'Artificial Intelligence', 'Machine Learning', 'Neural Networks', 'Data Science',
  'Information Technology', 'Cybersecurity', 'Robotics', 'Signal Processing',
  'Energy', 'Wind Energy', 'Solar Energy', 'Power Systems',
  'Civil Engineering', 'Mechanical Engineering', 'Materials Science',
  'Medicine', 'Bioinformatics', 'Economics', 'Environmental Science'
];

const ReviewerOptIn = ({ available, keywords = [], onAvailableChange, onKeywordsChange }) => {
  const [draft, setDraft] = useState('');

  const has = (kw) => keywords?.some(k => k?.toLowerCase() === kw?.toLowerCase());

  const addKeyword = (raw) => {
    const kw = raw?.replace(/\s+/g, ' ')?.trim();
    if (!kw || has(kw)) return;
    onKeywordsChange([...(keywords || []), kw]);
  };

  const removeKeyword = (kw) => {
    onKeywordsChange((keywords || [])?.filter(k => k !== kw));
  };

  const toggleSuggested = (field) => {
    has(field) ? removeKeyword(keywords?.find(k => k?.toLowerCase() === field?.toLowerCase())) : addKeyword(field);
  };

  const commitDraft = () => {
    if (draft?.trim()) { addKeyword(draft); setDraft(''); }
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {/* Toggle header */}
      <button
        type="button"
        onClick={() => onAvailableChange(!available)}
        className="w-full flex items-start gap-3 p-4 text-start hover:bg-muted/40 transition-colors"
      >
        <span
          className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            available ? 'bg-primary border-primary' : 'border-muted-foreground/50'
          }`}
        >
          {available && <Icon name="Check" size={14} color="white" />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-2">
            <Icon name="Search" size={15} className="text-primary" />
            <span className="text-sm font-semibold text-foreground">List me in the reviewer directory</span>
          </span>
          <span className="block text-xs text-muted-foreground mt-1">
            Conference chairs can find you and invite you to review papers in your areas of expertise.
          </span>
        </span>
      </button>

      {/* Expertise editor */}
      {available && (
        <div className="border-t border-border p-4 space-y-4">
          <div>
            <p className="text-xs font-medium text-foreground mb-2">Suggested fields</p>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTED_FIELDS?.map((field) => {
                const active = has(field);
                return (
                  <button
                    key={field}
                    type="button"
                    onClick={() => toggleSuggested(field)}
                    className={`px-2.5 py-1 text-xs font-medium border transition-colors ${
                      active
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border hover:text-foreground hover:border-primary/50'
                    }`}
                  >
                    {active && <Icon name="Check" size={11} className="inline me-1" />}
                    {field}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label htmlFor="reviewer-keyword-input" className="block text-xs font-medium text-foreground mb-2">Add your own keywords</label>
            <div className="flex gap-2">
              <input
                id="reviewer-keyword-input"
                type="text"
                value={draft}
                onChange={(e) => setDraft(e?.target?.value)}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' || e?.key === ',') { e.preventDefault(); commitDraft(); }
                }}
                placeholder="e.g. Structural Health Monitoring"
                className="flex-1 h-10 rounded-lg border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <button
                type="button"
                onClick={commitDraft}
                disabled={!draft?.trim()}
                className="px-3 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>

          {keywords?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {keywords?.map((kw) => (
                <span key={kw} className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-primary/10 text-primary">
                  {kw}
                  <button type="button" onClick={() => removeKeyword(kw)} className="hover:text-error">
                    <Icon name="X" size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReviewerOptIn;
